/**
 * iter552 — Empty Slot Placeholder
 *
 * Когато widget е скрит И dashboard-ът е в Edit Mode, на негово място
 * се показва интерактивен empty slot:
 *   • Показва коя секция е била тук (label + scope)
 *   • Dropdown с останалите скрити widgets за бърза замяна
 *   • "+ Върни тази секция" бутон за инстантно възстановяване
 *
 * Резултатът: dashboard-ът се държи като „blank canvas" — потребителят
 * може да премахне нещо и веднага да сложи друго на същото място.
 */
import React, { useState, useMemo } from "react";
import { Plus, ArrowRightLeft, RotateCcw } from "lucide-react";
import { DASHBOARD_WIDGETS } from "@/dashboard/widgetRegistry";
import { useDashboardLayoutContext } from "@/dashboard/DashboardLayoutContext";
import { useTheme } from "@/theme/ThemeContext";
import { useLanguage } from "@/i18n/LanguageContext";

export default function EmptySlot({ widgetId }) {
  const { layout, setHidden } = useDashboardLayoutContext();
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const bg = language === "bg";
  const [open, setOpen] = useState(false);

  const meta = DASHBOARD_WIDGETS.find((w) => w.id === widgetId);
  const label = meta ? (bg ? meta.label_bg : meta.label_en) : widgetId;

  // All hidden widgets that could be placed in this slot (including the
  // current one, since the user might just want to bring this exact one back)
  const otherHidden = useMemo(() => {
    const hidden = new Set(layout?.hidden || []);
    return DASHBOARD_WIDGETS.filter((w) => hidden.has(w.id));
  }, [layout]);

  const handlePick = (id) => {
    setHidden(id, false);
    setOpen(false);
  };

  return (
    <div
      data-dashboard-empty-slot={widgetId}
      data-testid={`empty-slot-${widgetId}`}
      className={`relative rounded-2xl border-2 border-dashed p-5 transition-colors ${
        isDark
          ? "border-slate-700 bg-slate-900/40 hover:bg-slate-900/60 hover:border-violet-500/50"
          : "border-slate-300 bg-slate-50/70 hover:bg-slate-50 hover:border-violet-400"
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${
          isDark ? "bg-violet-500/15 text-violet-300" : "bg-violet-100 text-violet-600"
        }`}>
          <Plus className="w-5 h-5" strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-[10px] uppercase font-bold tracking-wider mb-0.5 ${
            isDark ? "text-slate-500" : "text-slate-400"
          }`}>
            {bg ? "Свободна позиция" : "Empty slot"}
          </div>
          <h4 className={`text-[13px] font-bold ${isDark ? "text-slate-300" : "text-slate-700"}`}>
            {bg ? "Тук беше" : "Was here:"}{" "}
            <span className={isDark ? "text-violet-300" : "text-violet-700"}>{label}</span>
          </h4>
          <p className={`mt-1 text-[11px] leading-relaxed ${
            isDark ? "text-slate-400" : "text-slate-500"
          }`}>
            {bg
              ? "Можеш да върнеш тази секция или да сложиш друга на нейно място."
              : "Bring this one back, or pick another section to fill this spot."}
          </p>

          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setHidden(widgetId, false)}
              data-testid={`empty-slot-restore-${widgetId}`}
              className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-bold transition-all ${
                isDark
                  ? "bg-violet-500 text-white hover:bg-violet-400"
                  : "bg-violet-600 text-white hover:bg-violet-500"
              }`}
            >
              <RotateCcw className="w-3 h-3" />
              {bg ? "Върни тази" : "Bring back"}
            </button>

            {otherHidden.length > 1 && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setOpen((v) => !v)}
                  data-testid={`empty-slot-swap-${widgetId}`}
                  className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-bold border transition-colors ${
                    isDark
                      ? "border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700"
                      : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <ArrowRightLeft className="w-3 h-3" />
                  {bg ? "Избери друга секция" : "Pick another section"}
                </button>
                {open && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setOpen(false)}
                      aria-hidden="true"
                    />
                    <div
                      role="menu"
                      data-testid={`empty-slot-menu-${widgetId}`}
                      className={`absolute left-0 mt-1.5 w-64 z-50 rounded-xl border shadow-xl py-1 max-h-72 overflow-y-auto ${
                        isDark
                          ? "bg-slate-900 border-slate-700"
                          : "bg-white border-slate-200"
                      }`}
                    >
                      {otherHidden
                        .filter((w) => w.id !== widgetId)
                        .map((w) => (
                          <button
                            key={w.id}
                            type="button"
                            onClick={() => handlePick(w.id)}
                            data-testid={`empty-slot-pick-${w.id}`}
                            className={`block w-full text-left px-3 py-2 text-[12px] transition-colors ${
                              isDark
                                ? "hover:bg-violet-500/20 text-slate-200"
                                : "hover:bg-violet-50 text-slate-700"
                            }`}
                          >
                            <div className="font-semibold">
                              {bg ? w.label_bg : w.label_en}
                            </div>
                            <div className={`text-[10px] ${isDark ? "text-slate-500" : "text-slate-400"}`}>
                              {bg ? w.description_bg : w.description_en}
                            </div>
                          </button>
                        ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
